<template>
  <LoadingDiv v-if="!isReady" />
  <div v-else class="provider-manager">
    <header class="provider-header">
      <div><span>Network configuration</span><h1>Provider import</h1><p>Upload providers and assign every record to the correct HMO plan.</p></div>
      <div class="provider-header-icon"><Users :size="20" /></div>
    </header>

    <section v-if="counter.length" class="provider-results" aria-label="Completed imports">
      <article v-for="(count, id) in counter" :key="count.id || `${count.tariff}-${id}`" class="provider-result-card">
        <div class="provider-result-icon"><CheckCircle2 :size="18" /></div>
        <div><span>Import {{ id + 1 }}</span><h2>{{ count.tariff }}</h2><p>{{ count.value }} providers added to this tariff.</p></div>
      </article>
    </section>

    <form class="provider-form" @submit.prevent="handleSubmit">
      <div class="provider-form-heading"><FileSpreadsheet :size="20" /><div><h2>Import provider list</h2><p>Use an Excel workbook in XLSX or XLS format.</p></div></div>
      <div class="provider-field-grid">
        <label class="provider-field provider-file-field">
          <span>Provider list</span>
          <input type="file" accept=".xlsx,.xls" :disabled="isSubmitting" @change="handleFile" />
          <span class="provider-file-control"><Upload :size="16" /> {{ inputFile || "Choose Excel file" }}</span>
          <small v-if="fileError" class="provider-error">{{ fileError }}</small>
        </label>
        <label class="provider-field">
          <span>HMO plan</span>
          <select v-model="plan" :disabled="isSubmitting">
            <option :value="null" disabled>Select HMO plan</option>
            <option v-for="item in plans" :key="item.id" :value="item">{{ item.name }}</option>
          </select>
        </label>
      </div>
      <div v-if="uploadError" class="provider-error-box">{{ uploadError }}</div>
      <footer class="provider-form-footer">
        <span v-if="isSubmitting">Uploading {{ uploadProgress }}</span>
        <button class="provider-primary-button" :disabled="!file || !plan || isSubmitting"><Upload :size="16" />{{ isSubmitting ? `Uploading (${uploadProgress})` : "Upload providers" }}</button>
      </footer>
    </form>

    <footer class="provider-page-actions"><button type="button" class="provider-primary-button" @click="handleNext">Continue <ArrowRight :size="16" /></button></footer>
  </div>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from "vue";
import { ArrowRight, CheckCircle2, FileSpreadsheet, Upload, Users } from "@lucide/vue";
import getItems from "@/composables/getItems";
import useStorage from "@/composables/useStorage";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { adminAPI } from '@/laravel/config';

export default {
  name: "ProviderSetupComponent",
  components: { ArrowRight, CheckCircle2, FileSpreadsheet, LoadingDiv, Upload, Users },
  emits: ["next"],
  setup(props, context) {
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const response = ref(null);
    const error = ref(null);
    const isReady = ref(false);
    const isSubmitting = ref(false);
    const next = ref(false);
    const counter = ref([]);
    const plan = ref(null);
    const types = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];

    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);
        if (res.value) response.value = res.value.data;
        else error.value = err.value;
        isReady.value = true;
      })();
    };

    const { uploadFile, uploadProgress, uploadError, data, completed } = useStorage();
    onMounted(() => getItemsFromApi("plan/private"));

    const clearForm = () => {
      file.value = null;
      inputFile.value = null;
      isSubmitting.value = false;
      uploadProgress.value = "0%";
    };

    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
    };

    const handleSubmit = async () => {
      if (file.value && plan.value) {
        isSubmitting.value = true;
        uploadError.value = null;
        const fx = "providerLoader";
        const cloudInput = { plan_id: plan.value.id, endPoint: `https://api.welpro.easyhmo.com.ng/api/admin/provider/setup` };
        console.log(adminAPI);
        uploadFile(file.value, fx, cloudInput);
      } else clearForm();
    };

    watchEffect(() => {
      if (completed.value) {
        counter.value.push({ tariff: plan.value.name, value: data.value.succ.inserted_rows });
        clearForm();
      }
      if (uploadError.value) clearForm();
    });

    const handleNext = () => context.emit("next", { step: 5, isComplete: true });
    const plans = computed(() => response.value || []);

    return { handleFile, handleNext, handleSubmit, next, counter, isReady, isSubmitting, plan, uploadProgress, fileError, uploadError, file, inputFile, plans };
  },
};
</script>

<style scoped>
.provider-manager { display: grid; gap: 1rem; }
.provider-header { align-items: center; background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); display: flex; justify-content: space-between; padding: 1.35rem; }
.provider-header > div:first-child > span { color: var(--ui-primary); font-size: .7rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.provider-header h1 { color: var(--ui-foreground); font-family: var(--ui-font-display); font-size: 1.35rem; margin: .2rem 0; }
.provider-header p, .provider-form-heading p, .provider-result-card p { color: var(--ui-muted-foreground); margin: 0; }
.provider-header-icon, .provider-result-icon { align-items: center; background: var(--ui-secondary); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); display: flex; height: 2.5rem; justify-content: center; width: 2.5rem; }
.provider-results { display: grid; gap: .75rem; grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr)); }
.provider-result-card { align-items: flex-start; background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); display: flex; gap: .85rem; padding: 1rem; }
.provider-result-card span { color: var(--ui-muted-foreground); font-size: .7rem; font-weight: 650; text-transform: uppercase; }
.provider-result-card h2 { color: var(--ui-foreground); font-size: .95rem; margin: .15rem 0; }
.provider-result-icon { color: var(--ui-success); flex: 0 0 auto; }
.provider-form { background: var(--ui-card); border: 1px solid var(--ui-border); border-radius: var(--ui-radius); overflow: hidden; }
.provider-form-heading { align-items: center; border-bottom: 1px solid var(--ui-border); display: flex; gap: .75rem; padding: 1.2rem; }
.provider-form-heading h2 { color: var(--ui-foreground); font-size: 1rem; margin: 0 0 .15rem; }
.provider-field-grid { display: grid; gap: 1rem; grid-template-columns: repeat(2, minmax(0, 1fr)); padding: 1.2rem; }
.provider-field { display: grid; gap: .45rem; position: relative; }
.provider-field > span:first-child { color: var(--ui-foreground); font-size: .78rem; font-weight: 650; }
.provider-field select, .provider-file-control { align-items: center; background: var(--ui-background); border: 1px solid var(--ui-input); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-foreground); display: flex; gap: .5rem; min-height: 2.75rem; padding: .65rem .75rem; width: 100%; }
.provider-file-field input { inset: 1.65rem 0 0; opacity: 0; position: absolute; width: 100%; z-index: 1; }
.provider-file-control { color: var(--ui-muted-foreground); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.provider-error { color: var(--ui-destructive); font-size: .72rem; }
.provider-error-box { background: color-mix(in srgb, var(--ui-destructive) 8%, transparent); border-top: 1px solid var(--ui-border); color: var(--ui-destructive); padding: .8rem 1.2rem; }
.provider-form-footer, .provider-page-actions { align-items: center; border-top: 1px solid var(--ui-border); display: flex; gap: 1rem; justify-content: flex-end; padding: 1rem 1.2rem; }
.provider-form-footer > span { color: var(--ui-muted-foreground); font-size: .78rem; }
.provider-page-actions { border: 0; padding-inline: 0; }
.provider-primary-button { align-items: center; background: var(--ui-primary); border: 1px solid var(--ui-primary); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-primary-foreground); cursor: pointer; display: inline-flex; font-size: .8rem; font-weight: 650; gap: .5rem; justify-content: center; min-height: 2.5rem; padding: 0 1rem; }
.provider-primary-button:disabled { cursor: not-allowed; opacity: .5; }
@media (max-width: 640px) { .provider-field-grid { grid-template-columns: 1fr; } .provider-header { align-items: flex-start; } .provider-header-icon { display: none; } .provider-form-footer { align-items: stretch; flex-direction: column; } .provider-primary-button { width: 100%; } }
</style>
